import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { AdminContinuityCard } from '@/components/client/AdminContinuityCard';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Globe, MapPin, Phone, ShieldCheck } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import CompanyProfileForm from '@/components/shared/CompanyProfileForm';

// UAT 07/18 — thin wrapper: the corporate profile lives in the shared
// CompanyProfileForm component (same one used by HM Admin/Advisor).
// Founder UAT Wave 1 — executive hero header on top of the shared form.
export default function ClientCompanyProfile() {
  const { t } = useTranslation();
  const { user } = useApp();
  const isCompanyAdmin = user?.role === 'client_admin';
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/client/me/company');
        setCompany(data);
      } catch (e) {
        toast.error(t('pages.companyProfile.loadFailed', { defaultValue: 'Could not load the company profile' }),
          { description: formatApiError(e) });
      } finally { setLoading(false); }
    })();
  }, [t]);

  const onSave = async (body) => {
    const { data } = await api.patch('/client/me/company', body);
    setCompany(data);
    return data;
  };

  const heroChips = company ? [
    { icon: MapPin, value: [company.city, company.country].filter(Boolean).join(', '), testid: 'cp-hero-location' },
    { icon: Globe, value: company.website, testid: 'cp-hero-website' },
    { icon: Phone, value: company.phone, testid: 'cp-hero-phone' },
  ].filter((c) => c.value) : [];

  return (
    <>
      <Topbar
        title={t('pages.companyProfile.title', { defaultValue: 'Company Profile' })}
        subtitle={t('pages.companyProfile.subtitle', { defaultValue: 'Corporate information of your organization' })}
      />
      <div className="p-6 lg:p-8 max-w-5xl space-y-6">
        <AdminContinuityCard />
        {loading ? (
          <Card className="p-12 text-center text-sm text-muted-foreground" data-testid="company-profile-loading">
            {t('common.loading')}
          </Card>
        ) : (
          <>
            {/* Executive hero header */}
            <Card className="p-6 lg:p-8 shadow-card bg-gradient-hero text-white relative overflow-hidden" data-testid="company-profile-hero">
              <div className="absolute inset-0 dot-pattern opacity-[0.08]" />
              <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="h-20 w-20 rounded-xl bg-white shadow-soft flex items-center justify-center overflow-hidden p-2 shrink-0" data-testid="cp-hero-logo">
                  {company?.logo_url
                    ? <img src={company.logo_url} alt={company?.name} className="h-full w-full object-contain" />
                    : <Building2 className="h-9 w-9 text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                  <Badge className="bg-white/10 text-white border-white/20 hover:bg-white/15 font-medium mb-2">
                    <ShieldCheck className="h-3 w-3 mr-1.5" />
                    {t('pages.companyProfile.hero.badge', { defaultValue: 'Corporate profile' })}
                  </Badge>
                  <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight" data-testid="cp-hero-name">
                    {company?.name || '—'}
                  </h2>
                  {company?.legal_name && company.legal_name !== company.name && (
                    <p className="text-white/70 text-sm mt-0.5" data-testid="cp-hero-legal-name">{company.legal_name}</p>
                  )}
                  {heroChips.length > 0 && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3">
                      {heroChips.map(({ icon: Icon, value, testid }) => (
                        <span key={testid} className="inline-flex items-center gap-1.5 text-xs text-white/80" data-testid={testid}>
                          <Icon className="h-3.5 w-3.5" /> {value}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Card>
            <CompanyProfileForm company={company} onSave={onSave} readOnly={!isCompanyAdmin} />
          </>
        )}
      </div>
    </>
  );
}
